import React, { useState, useContext, useEffect, useRef } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';

import { DeepCopy } from '../../tables/shared/TableEventHelpers';
import { GetWindow } from '../requests/CommonRequests';
import { HandlePatchDeal } from '../requests/DealRequests';
import { Debouncer, CalculateSettings, updateDealProps } from '../shared/Functions';
import { HeaderCell } from '../shared/TableCells';
import Column from '../Column';
import NoDataCrm from '../shared/NoDataCrm';
import { applyFilter } from './DealFilterHelpers';

import { DataContext as CrmDataContext } from '../../../stores/crm-store';
import '../../../styles/pages/crm.scss';

// Reference: https://blog.logrocket.com/virtual-scrolling-core-principles-and-basic-implementation-in-react/
const VirtualScrollerDeal = ({ dataContext, pipelineId }) => {
    const regularStore = useContext(dataContext);
    const crmStore = useContext(CrmDataContext);

    const {
        userFilterType,
        selectedUsersOptions,
        selectedUserGroupOption,
        selectedBossGroupOption,
        selectedTeamGroupOption,
        apiUrl,
        pipelines,
        currentPipelineId,
        activeDeal,
    } = regularStore.store;
    const dispatch = regularStore.dispatch;

    const [oldIndex, setOldIndex] = useState(0);
    const [index, setIndex] = useState(0);
    const [isLoadingWindow, setIsLoadingWindow] = useState(false);
    const debouncedIndex = Debouncer(index, 150);

    const [filterUpdating, setFilterUpdating] = useState(false);
    const previousFilter = useRef(pipelines[currentPipelineId].requestObject.filters);

    const dataExists = () => {
        return pipelines[currentPipelineId].pipelineStages.some((stage) => stage.deals && stage.deals.length > 0);
    };

    const finishedWindowGet = (res) => {
        if (res.success) {
            let newPipelineStages = DeepCopy(pipelines[currentPipelineId].pipelineStages);
            let totalCounts = [];

            //Asuming the incoming data and existing is in the correct order, which it should be
            for (let i = 0; i < newPipelineStages.length; i++) {
                newPipelineStages[i].deals = res.data[i].dataEntities;
                totalCounts.push(res.data[i].totalCount);
            }

            pipelines[currentPipelineId].viewSettings.maxIndex = Math.max(...totalCounts);
            pipelines[currentPipelineId].viewSettings.startIndex = debouncedIndex;

            let newViewSettings = CalculateSettings(pipelines[currentPipelineId].viewSettings, pipelines[currentPipelineId].requestObject);
            pipelines[currentPipelineId].viewSettings = newViewSettings;
            pipelines[currentPipelineId].pipelineStages = newPipelineStages;
            dispatch({
                type: 'UPDATE_FETCH_PIPES',
                pipelines: pipelines,
            });
        }
        setIsLoadingWindow(false);
        setFilterUpdating(false);
    };

    const handleVirtualScroll = (e) => {
        let scrollTop = e.target.scrollingElement.scrollTop - 140;
        let minIndex = pipelines[currentPipelineId].viewSettings.minIndex;
        let toleranceHeight = pipelines[currentPipelineId].viewSettings.tolerance * pipelines[currentPipelineId].viewSettings.itemHeight;
        let itemHeight = pipelines[currentPipelineId].viewSettings.itemHeight;
        let tolerance = pipelines[currentPipelineId].viewSettings.tolerance;

        setIndex(minIndex + Math.floor(((scrollTop >= 0 ? scrollTop : 0) - toleranceHeight) / itemHeight) + tolerance);
    };

    const updateResult = (res) => {
        res.success ? crmStore.dispatch({ type: 'SHOW_SAVED_TOAST' }) : crmStore.dispatch({ type: 'SHOW_ERROR_TOAST' });
    };

    // Update fetch settings
    const handleUpdateUsers = (filterKey, users) => {
        let newFilterUsers = [];
        newFilterUsers = users.map((item) => {
            if (item == null) {
                return null;
            } else {
                return {
                    id: item.id,
                    name: item.name,
                };
            }
        });
        const newSerializedFilterUsers = JSON.stringify(newFilterUsers);
        pipelines[currentPipelineId].filterOptions['Users'] = newSerializedFilterUsers;
        applyFilter(filterKey, newSerializedFilterUsers, dispatch, pipelines, currentPipelineId);
    };

    useEffect(
        () => {
            localStorage.setItem(`deal-user-filter-type`, JSON.stringify(userFilterType));
            switch (userFilterType.id) {
                case 'user':
                    localStorage.setItem(`deal-selected-users-options`, JSON.stringify(selectedUsersOptions ?? []));
                    handleUpdateUsers('Users', selectedUsersOptions ?? []);
                    break;
                case 'boss':
                    localStorage.setItem(`deal-selected-boss-group-option`, JSON.stringify(selectedBossGroupOption ?? []));
                    handleUpdateUsers('Boss', [selectedBossGroupOption] ?? []);
                    break;
                case 'group':
                    localStorage.setItem(`deal-selected-user-group-option`, JSON.stringify(selectedUserGroupOption ?? []));
                    handleUpdateUsers('Group', [selectedUserGroupOption] ?? []);
                    break;
                case 'team':
                    localStorage.setItem(`deal-selected-team-group-option`, JSON.stringify(selectedTeamGroupOption ?? []));
                    handleUpdateUsers('Users', selectedTeamGroupOption?.users ?? []);
                    break;
                default:
                    break;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [userFilterType, selectedUsersOptions, selectedBossGroupOption, selectedUserGroupOption, selectedTeamGroupOption]
    );

    async function handleDragEnd(result, pipelineStages, dispatch) {
        if (!result.destination) return;
        const { source, destination } = result;
        const sourceColumn = pipelineStages[source.droppableId];
        const destColumn = pipelineStages[destination.droppableId];
        const sourceItems = [...sourceColumn.deals];
        const destItems = [...destColumn.deals];

        var [removed] = sourceItems.splice(source.index, 1);
        var original = DeepCopy(removed);
        removed['pipelineStageId'] = pipelineStages[destination.droppableId]['id'];

        if (source.droppableId !== destination.droppableId) {
            destItems.splice(destination.index, 0, removed);

            pipelines[currentPipelineId].pipelineStages = Object.values({
                ...pipelineStages,
                [source.droppableId]: { ...sourceColumn, deals: sourceItems },
                [destination.droppableId]: { ...destColumn, deals: destItems },
            });

            dispatch({
                type: 'UPDATE_FETCH_PIPES',
                pipelines: pipelines,
            });

            const response = await HandlePatchDeal(original, removed);
            if (activeDeal !== null && activeDeal.id === response.deal.id) {
                dispatch({
                    type: 'UPDATE_ACTIVE_DEAL',
                    payload: response.deal,
                });
            }
            updateResult(response);
        } else {
            sourceItems.splice(destination.index, 0, removed);

            pipelines[currentPipelineId].pipelineStages = Object.values({
                ...pipelineStages,
                [source.droppableId]: { ...sourceColumn, deals: sourceItems },
            });

            dispatch({
                type: 'UPDATE_FETCH_PIPES',
                pipelines: pipelines,
            });
        }
    }

    useEffect(() => {
        let stickyElement = document.querySelector('.sticky-element');

        if (stickyElement !== undefined && stickyElement !== null) {
            let parent = stickyElement.parentElement;

            while (parent) {
                const hasOverflow = getComputedStyle(parent).overflow;
                if (hasOverflow !== 'visible') {
                    parent.overflow = 'visible';
                }
                parent = parent.parentElement;
            }
        }
    }, []);

    useEffect(() => {
        if (pipelines[currentPipelineId].initialGet === true) {
            dispatch({
                type: 'UPDATE_FETCH_PIPES',
                pipelines: pipelines,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pipelineId]);

    useEffect(() => {
        //console.log("updating pipelines");
        if (pipelines[currentPipelineId].requestObject !== null && pipelines[currentPipelineId].initialGet) {
            setIsLoadingWindow(true);
            GetWindow(apiUrl, pipelines[currentPipelineId].requestObject, finishedWindowGet);
            pipelines[currentPipelineId].initialGet = false;

            dispatch({
                type: 'UPDATE_FETCH_PIPES',
                pipelines: pipelines,
            });
        } else if (JSON.stringify(previousFilter.current) !== JSON.stringify(pipelines[currentPipelineId].requestObject.filters)) {
            // If filter has been changed
            previousFilter.current = pipelines[currentPipelineId].requestObject.filters;
            setFilterUpdating(true);
            GetWindow(apiUrl, pipelines[currentPipelineId].requestObject, finishedWindowGet);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pipelines[currentPipelineId].requestObject]);

    useEffect(() => {
        function watchScroll() {
            window.addEventListener('scroll', handleVirtualScroll);
        }
        watchScroll();
        return () => {
            window.removeEventListener('scroll', handleVirtualScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    });

    useEffect(() => {
        if (debouncedIndex !== oldIndex) {
            setOldIndex(debouncedIndex);
            const len = pipelines[currentPipelineId].requestObject.amount + 2 * pipelines[currentPipelineId].viewSettings.tolerance;

            let newRequestObject = DeepCopy(pipelines[currentPipelineId].requestObject);
            newRequestObject.startIndex = debouncedIndex;

            const topPaddingHeight = Math.max(
                (debouncedIndex - pipelines[currentPipelineId].viewSettings.minIndex) *
                pipelines[currentPipelineId].viewSettings.itemHeight,
                0
            );
            const bottomPaddingHeight = Math.max(
                pipelines[currentPipelineId].viewSettings.totalHeight -
                topPaddingHeight -
                len * pipelines[currentPipelineId].viewSettings.itemHeight,
                0
            );

            let newSettings = DeepCopy(pipelines[currentPipelineId].viewSettings);
            pipelines[currentPipelineId].viewSettings.topPaddingHeight = topPaddingHeight;
            pipelines[currentPipelineId].viewSettings.bottomPaddingHeight = bottomPaddingHeight;

            pipelines[currentPipelineId].viewSettings = newSettings;
            pipelines[currentPipelineId].requestObject = newRequestObject;

            dispatch({
                type: 'UPDATE_FETCH_PIPES',
                pipelines: pipelines,
            });

            setIsLoadingWindow(true);
            GetWindow(apiUrl, newRequestObject, finishedWindowGet);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedIndex]);

    return (
        <div className='d-flex flex-column justify-content-center'>
            <div className='d-flex flex-row sticky-element mb-2'>
                {Object.entries(pipelines[currentPipelineId].pipelineStages && pipelines[currentPipelineId].pipelineStages).map(
                    ([columnId, column]) => {
                        return (
                            <HeaderCell
                                key={columnId + '-header'}
                                textContent={column.name}
                                width={(100.0 / pipelines[currentPipelineId].pipelineStages.length).toString() + '%'}
                            />
                        );
                    }
                )}
            </div>
            <div
                style={{ height: pipelines[currentPipelineId].viewSettings.totalHeight }}
                className={`d-flex flex-row ${isLoadingWindow ? 'pulsating-background' : '' || filterUpdating ? 'pulsating-background' : ''
                    } `}>
                {!dataExists() && !isLoadingWindow ? <NoDataCrm rows={[]} /> : 
                <DragDropContext onDragEnd={(result) => handleDragEnd(result, pipelines[currentPipelineId].pipelineStages, dispatch)}>
                    <div className='w-100 d-flex flex-row justify-content-around'>
                        {Object.entries(pipelines[currentPipelineId].pipelineStages && pipelines[currentPipelineId].pipelineStages).map(
                            ([columnId, column]) => {
                                return (
                                    <div
                                        key={'column-' + columnId}
                                        className='d-flex flex-column'
                                        style={{ width: (100.0 / pipelines[currentPipelineId].pipelineStages.length).toString() + '%' }}>
                                        <Column key={columnId} dataContext={dataContext} column={column} columnId={columnId} />
                                    </div>
                                );
                            }
                        )}
                    </div>
                </DragDropContext>}
            </div>
        </div>
    );
};

export default VirtualScrollerDeal;
